"use client";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createSupabaseBrowserClient } from "../lib/auth/supabase-browser";

interface SigninInput {
  email: string;
  password: string;
}

interface SigninResult {
  userId: string | null;
  error?: string;
}

async function signinAndEnsureProfile(
  input: SigninInput
): Promise<SigninResult> {
  const supabase = createSupabaseBrowserClient();
  const { data, error } = await supabase.auth.signInWithPassword({
    email: input.email,
    password: input.password,
  });
  if (error) {
    throw new Error(error.message);
  }
  const accessToken = data.session?.access_token;
  // Ensure profile row exists (id == auth user id). Empty body: creation if missing.
  if (accessToken) {
    await fetch("/api/auth/profile", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({}),
    }).catch(() => {});
  }
  return { userId: data.user?.id || null };
}

export function useSignin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: signinAndEnsureProfile,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["profile"] });
    },
  });
}
